import { createElement, useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
    AppBar,
    Box,
    CircularProgress,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Modal,
    Toolbar,
    Typography,
} from "@mui/material";
import {
    AutoAwesome,
    // AttachMoney,
    Close,
    MonetizationOn,
    ElectricalServices,
    Code,
    History,
    Logout,
    Menu,
    Science,
    Settings,
    Campaign,
} from "@mui/icons-material";

import { useAtom } from "jotai";
import { globalStore } from "../store/global.atoms";

import ChikoLogo from "../static/chiko_logo.png";

const drawerWidth = 240;
const PLAYGROUND_INSTRUCTIONS_DISMISSED_KEY = "playgroundInstructionsDismissed";

function InternalMenu({ children }) {
    const navigate = useNavigate();

    const backendEndpoint = process.env.REACT_APP_BACKEND_ENDPOINT;
    // const stripePortalUrl = process.env.REACT_APP_STRIPE_PORTAL_URL;
    const stripePricingTableId = process.env.REACT_APP_STRIPE_PRICING_TABLE_ID;
    const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

    const [userData, setUserData] = useAtom(globalStore.userDataAtom);
    const [billingData, setBillingData] = useAtom(globalStore.billingDataAtom);

    const email = userData?.email;
    // const plan = userData?.plan.split("-")[0];
    // const usageLimit = userData?.usageLimit;

    const [mobileOpen, setMobileOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);

    const [openPricingTable, setOpenPricingTable] = useState(false);

    const handleDrawerClose = () => {
        setIsClosing(true);
        setMobileOpen(false);
    };

    const handleDrawerTransitionEnd = () => {
        setIsClosing(false);
    };

    const handleDrawerToggle = () => {
        if (!isClosing) {
            setMobileOpen(!mobileOpen);
        }
    };

    // const handleManagePlan = () => {
    //     if (plan.toLowerCase().includes("trial")) {
    //         setOpenPricingTable(true);
    //     } else {
    //         window.open(`${stripePortalUrl}?prefilled_email=${email}`);
    //     }
    // };

    const handleClosePricingTable = () => {
        setOpenPricingTable(false);
    };

    const getCookieValue = useCallback(
        (name) =>
            document.cookie
                .match("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)")
                ?.pop() || "",
        [],
    );

    const authenticate = useCallback(async () => {
        const response = await fetch(`${backendEndpoint}/authenticate/`, {
            method: "POST",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": getCookieValue("csrfToken"),
            },
        });

        const json = await response.json();

        if (json.authenticated) {
            setUserData(json);
            return json.authenticated;
        } else {
            const authenticated = false;
            return authenticated;
        }
    }, [backendEndpoint, getCookieValue, setUserData]);

    const handleLogout = async () => {
        const response = await fetch(`${backendEndpoint}/logout/`, {
            method: "POST",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": getCookieValue("csrfToken"),
            },
        });

        const { logout } = await response.json();

        if (logout) {
            sessionStorage.removeItem(PLAYGROUND_INSTRUCTIONS_DISMISSED_KEY);
            navigate("/");
        }
    };

    const loadBillingData = useCallback(async () => {
        const res = await fetch(`${backendEndpoint}/billing/`, {
            credentials: "include",
            headers: { "X-CSRF-Token": getCookieValue("csrfToken") },
        });

        if (res.ok) {
            const json = await res.json();
            setBillingData(json);
        }
    }, [backendEndpoint, getCookieValue, setBillingData]);

    useEffect(() => {
        const run = async () => {
            const authenticated = await authenticate();
            if (!authenticated) {
                navigate("/");
                return;
            }
            loadBillingData();
        };
        run();
    }, [authenticate, navigate, loadBillingData]);

    useEffect(() => {
        const script = document.createElement("script");
        script.src = "https://js.stripe.com/v3/pricing-table.js";
        script.async = true;

        document.body.appendChild(script);

        return () => document.body.removeChild(script);
    }, []);

    const drawer = (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <Toolbar sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    <img src={ChikoLogo} alt="Chiko Logo" height="50" />

                    <Typography
                        sx={{
                            mt: 1,
                            fontSize: 20,
                            fontWeight: "bold",
                            overflow: "hidden",
                            maxWidth: 150,
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                        }}
                        title={userData?.companyName}
                    >
                        {userData?.companyName}
                    </Typography>
                </Box>
            </Toolbar>
            <Divider sx={{ mt: 4 }} />
            <Box
                sx={{
                    flexGrow: 1,
                    overflowY: "auto",
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                }}
            >
                <List>
                    <ListItem key="Treinamento da IA" disablePadding>
                        <Link
                            id="menu-contents"
                            to="/training"
                            style={{
                                width: "100%",
                                textDecoration: "none",
                                color: "inherit",
                            }}
                        >
                            <ListItemButton
                                sx={{
                                    "&:hover": {
                                        backgroundColor: "#efefef",
                                        color: "#0359D6",
                                    },
                                }}
                            >
                                <ListItemIcon sx={{ color: "inherit" }}>
                                    <AutoAwesome />
                                </ListItemIcon>
                                <ListItemText primary="Treinamento da IA" />
                            </ListItemButton>
                        </Link>
                    </ListItem>
                    <ListItem key="Chat" disablePadding>
                        <Link
                            id="menu-chat"
                            to={`/chat`}
                            style={{
                                width: "100%",
                                textDecoration: "none",
                                color: "inherit",
                            }}
                        >
                            <ListItemButton
                                sx={{
                                    "&:hover": {
                                        backgroundColor: "#efefef",
                                        color: "#0359D6",
                                    },
                                }}
                            >
                                <ListItemIcon sx={{ color: "inherit" }}>
                                    <Science />
                                </ListItemIcon>
                                <ListItemText primary="Playground" />
                            </ListItemButton>
                        </Link>
                    </ListItem>
                    <ListItem key="Histórico" disablePadding>
                        <Link
                            id="menu-history"
                            to="/history"
                            style={{
                                width: "100%",
                                textDecoration: "none",
                                color: "inherit",
                            }}
                        >
                            <ListItemButton
                                sx={{
                                    "&:hover": {
                                        backgroundColor: "#efefef",
                                        color: "#0359D6",
                                    },
                                }}
                            >
                                <ListItemIcon sx={{ color: "inherit" }}>
                                    <History />
                                </ListItemIcon>
                                <ListItemText primary="Histórico" />
                            </ListItemButton>
                        </Link>
                    </ListItem>
                    <ListItem key="Integrações" disablePadding>
                        <Link
                            id="menu-integrations"
                            to="/integrations"
                            style={{
                                width: "100%",
                                textDecoration: "none",
                                color: "inherit",
                            }}
                        >
                            <ListItemButton
                                sx={{
                                    "&:hover": {
                                        backgroundColor: "#efefef",
                                        color: "#0359D6",
                                    },
                                }}
                            >
                                <ListItemIcon sx={{ color: "inherit" }}>
                                    <Code />
                                </ListItemIcon>
                                <ListItemText primary="Integrações" />
                            </ListItemButton>
                        </Link>
                    </ListItem>
                    <ListItem key="Chiko Connect" disablePadding>
                        <Link
                            id="menu-chiko-connect"
                            to="/chiko-connect"
                            style={{
                                width: "100%",
                                textDecoration: "none",
                                color: "inherit",
                            }}
                        >
                            <ListItemButton
                                sx={{
                                    "&:hover": {
                                        backgroundColor: "#efefef",
                                        color: "#0359D6",
                                    },
                                }}
                            >
                                <ListItemIcon sx={{ color: "inherit" }}>
                                    <ElectricalServices />
                                </ListItemIcon>
                                <ListItemText primary="Chiko Connect" />
                            </ListItemButton>
                        </Link>
                    </ListItem>
                    <ListItem key="Campanhas" disablePadding>
                        <Link
                            id="menu-campaigns"
                            to="/campaigns"
                            style={{
                                width: "100%",
                                textDecoration: "none",
                                color: "inherit",
                            }}
                        >
                            <ListItemButton
                                sx={{
                                    "&:hover": {
                                        backgroundColor: "#efefef",
                                        color: "#0359D6",
                                    },
                                }}
                            >
                                <ListItemIcon sx={{ color: "inherit" }}>
                                    <Campaign />
                                </ListItemIcon>
                                <ListItemText primary="Campanhas" />
                            </ListItemButton>
                        </Link>
                    </ListItem>
                </List>
            </Box>
            <Box>
                <Divider />
                <List>
                    <ListItem key="Consumo" disablePadding>
                        <Link
                            id="menu-billing"
                            to="/billing"
                            style={{
                                width: "100%",
                                textDecoration: "none",
                                color: "inherit",
                            }}
                        >
                            <ListItemButton
                                sx={{
                                    "&:hover": {
                                        backgroundColor: "#efefef",
                                        color: "#0359D6",
                                    },
                                    "&:hover .gaugeIndicator": {
                                        color: "#0359D6",
                                    },
                                    "&:hover .gaugeTrack": {
                                        color: "rgba(3,89,214,0.2)",
                                    },
                                    "&:hover .gaugePctText": {
                                        color: "#0359D6",
                                    },
                                }}
                            >
                                <ListItemIcon sx={{ color: "inherit" }}>
                                    {billingData?.credit?.total > 0 ? (
                                        (() => {
                                            const used =
                                                billingData.credit.used;
                                            const total =
                                                billingData.credit.total;
                                            const realPct =
                                                (used / total) * 100;
                                            const pct = Math.max(
                                                Math.min(realPct, 100),
                                                10,
                                            );
                                            const gaugeColor =
                                                realPct > 100
                                                    ? "#b71c1c"
                                                    : realPct >= 80
                                                      ? "#ff5722"
                                                      : realPct >= 50
                                                        ? "#ff9800"
                                                        : "#4caf50";
                                            return (
                                                <Box
                                                    className="gaugeIndicator"
                                                    sx={{
                                                        position: "relative",
                                                        display: "inline-flex",
                                                        color: gaugeColor,
                                                    }}
                                                >
                                                    <CircularProgress
                                                        variant="determinate"
                                                        value={100}
                                                        size={24}
                                                        thickness={4}
                                                        className="gaugeTrack"
                                                        sx={{
                                                            color: "rgba(255,255,255,0.3)",
                                                            position:
                                                                "absolute",
                                                        }}
                                                    />
                                                    <CircularProgress
                                                        variant="determinate"
                                                        value={pct}
                                                        size={24}
                                                        thickness={4}
                                                        sx={{
                                                            color: "inherit",
                                                        }}
                                                    />
                                                    <Box
                                                        sx={{
                                                            top: 0,
                                                            left: 0,
                                                            bottom: 0,
                                                            right: 0,
                                                            position:
                                                                "absolute",
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            justifyContent:
                                                                "center",
                                                        }}
                                                    >
                                                        <Typography
                                                            className="gaugePctText"
                                                            sx={{
                                                                fontSize: 7,
                                                                color: "#ffffff",
                                                            }}
                                                        >
                                                            {Math.round(
                                                                (used / total) *
                                                                    100,
                                                            )}
                                                            %
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            );
                                        })()
                                    ) : (
                                        <MonetizationOn />
                                    )}
                                </ListItemIcon>
                                <ListItemText primary="Consumo" />
                            </ListItemButton>
                        </Link>
                    </ListItem>
                    <ListItem key="Configurações" disablePadding>
                        <Link
                            to="/settings"
                            style={{
                                width: "100%",
                                textDecoration: "none",
                                color: "inherit",
                            }}
                        >
                            <ListItemButton
                                sx={{
                                    "&:hover": {
                                        backgroundColor: "#efefef",
                                        color: "#0359D6",
                                    },
                                }}
                            >
                                <ListItemIcon sx={{ color: "inherit" }}>
                                    <Settings />
                                </ListItemIcon>
                                <ListItemText primary="Configurações" />
                            </ListItemButton>
                        </Link>
                    </ListItem>
                    {/* <ListItem key="Gerenciar Plano" disablePadding>
                        <ListItemButton
                            onClick={() => handleManagePlan()}
                            sx={{
                                "&:hover": {
                                    backgroundColor: "#efefef",
                                    color: "#0359D6",
                                },
                                "& .MuiListItemText-secondary": {
                                    color: "#dedede",
                                },
                                "&:hover .MuiListItemText-secondary": {
                                    color: "#6B96D6",
                                },
                            }}
                        >
                            <ListItemIcon sx={{ color: "inherit" }}>
                                <AttachMoney />
                            </ListItemIcon>
                            <ListItemText
                                primary="Gerenciar Plano"
                                secondary={
                                    userData ? `${plan}: ${usageLimit}` : ""
                                }
                            />
                        </ListItemButton>
                    </ListItem> */}
                    <ListItem key="Sair" disablePadding>
                        <ListItemButton
                            onClick={() => handleLogout()}
                            sx={{
                                "&:hover": {
                                    backgroundColor: "#efefef",
                                    color: "#0359D6",
                                },
                            }}
                        >
                            <ListItemIcon sx={{ color: "inherit" }}>
                                <Logout />
                            </ListItemIcon>
                            <ListItemText primary="Sair" />
                        </ListItemButton>
                    </ListItem>
                </List>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: "flex" }}>
            <AppBar
                position="fixed"
                sx={{
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    ml: { sm: `${drawerWidth}px` },
                    display: { sm: "none" },
                    backgroundColor: "#0359D6",
                    color: "#ffffff",
                }}
            >
                <Toolbar>
                    <IconButton
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2 }}
                    >
                        <Menu />
                    </IconButton>
                    <img src={ChikoLogo} alt="Chiko Logo" height="50" />
                </Toolbar>
            </AppBar>
            <Box
                component="nav"
                sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
            >
                {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onTransitionEnd={handleDrawerTransitionEnd}
                    onClose={handleDrawerClose}
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile.
                    }}
                    sx={{
                        display: { xs: "block", sm: "none" },
                        "& .MuiDrawer-paper": {
                            boxSizing: "border-box",
                            width: drawerWidth,
                        },
                    }}
                >
                    {drawer}
                </Drawer>
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: "none", sm: "block" },
                        "& .MuiDrawer-paper": {
                            boxSizing: "border-box",
                            width: drawerWidth,
                            backgroundColor: "#0359D6",
                            color: "#ffffff",
                        },
                    }}
                    open
                >
                    {drawer}
                </Drawer>
            </Box>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    height: "100vh",
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                }}
            >
                <Toolbar sx={{ display: { sm: "none" } }} />
                {children}
            </Box>

            {/* Payment modal */}
            <Modal
                open={openPricingTable}
                // onClose={handleClosePricingTable}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    p: 4,
                }}
            >
                <Box
                    sx={{
                        maxHeight: "100%",
                        width: "100%",
                        maxWidth: 460,
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 4,
                        position: "relative",
                        overflow: "auto",
                    }}
                >
                    <IconButton
                        edge="end"
                        aria-label="close"
                        onClick={handleClosePricingTable}
                        sx={{
                            position: "absolute",
                            top: 10,
                            right: 20,
                        }}
                    >
                        <Close />
                    </IconButton>

                    {createElement("stripe-pricing-table", {
                        "pricing-table-id": stripePricingTableId,
                        "publishable-key": stripePublishableKey,
                        "customer-email": email,
                    })}
                </Box>
            </Modal>
        </Box>
    );
}

export default InternalMenu;
